<script setup lang="ts">
import { computed } from "vue";
import FilterDialog from "@/modules/explorer/components/FilterDialog/FilterDialog.vue";
import { useSegmentBuilderStore } from "@/modules/segments/store";
import { dimensionsForSegmentFilter } from "./segmentFilterDimensions";
import ActiveFilterDTO = App.Data.Explorer.Filter.ActiveFilterDTO;
import DimensionDO = App.Data.DimensionDO;
import SegmentPartDO = App.Data.Segments.SegmentPartDO;

const {
	filterToEdit = null,
	allAvailableDimensions,
	filterDataSource,
	filterPartType,
	isExpertMode,
} = defineProps<{
	filterToEdit?: ActiveFilterDTO | null;
	allAvailableDimensions: DimensionDO[];
	filterDataSource: string;
	filterPartType: string;
	isExpertMode: boolean;
}>();

const emit = defineEmits(["close-filter-bar"]);

const open = defineModel<boolean>({ default: false });

const segmentBuilderState = useSegmentBuilderStore().segmentBuilderState;

const availableDimensions = computed(() =>
	dimensionsForSegmentFilter(allAvailableDimensions, {
		dataSource: filterDataSource,
		isExpertMode,
	})
);

const partForFilter = computed<SegmentPartDO | null>(
	() =>
		segmentBuilderState.segment.parts.find(
			(part) =>
				part.data_source === filterDataSource && part.type === filterPartType
		) ?? null
);

/**
 * Scoped to the part being edited: the same dimension may legitimately carry a
 * different filter in the include and exclude parts. Handing them over lets the
 * dialog load an existing filter for editing rather than starting blank and
 * silently replacing it on apply.
 */
const activeFilters = computed(() => partForFilter.value?.active_filters ?? []);

const applyFilter = (activeFilter: ActiveFilterDTO) => {
	if (
		!segmentBuilderState.supportedDataSourcesInSegmentBuilder.includes(
			filterDataSource
		)
	) {
		return;
	}

	const part = partForFilter.value;

	if (!part) {
		segmentBuilderState.segment.parts.push({
			type: filterPartType,
			data_source: filterDataSource,
			active_filters: [activeFilter],
		} as SegmentPartDO);

		return;
	}

	part.active_filters = part.active_filters.filter(
		(filter) => filter.dimension.id !== activeFilter.dimension.id
	);

	// periodsubjectdateday has to lead, or the visualisation of filter effects
	// further down the part makes no sense.
	if (activeFilter.dimension.name_clean === "periodsubjectdateday") {
		part.active_filters.unshift(activeFilter);
		return;
	}

	part.active_filters.push(activeFilter);
};
</script>
<template>
	<filter-dialog
		v-model="open"
		:available-dimensions="availableDimensions"
		:active-filters="activeFilters"
		:selected-filter="filterToEdit"
		:current-segment-id="segmentBuilderState.segment?.id ?? null"
		api-client="segments"
		@apply="applyFilter"
		@close="emit('close-filter-bar')"
	/>
</template>
